/**
 * @file StockMovementsPanel.tsx
 * @description Журнал движений склада: фильтры, итог по отбору, строки
 * (phase19.13 этап 0; фильтры и «Основание» — phase19.24 этап 1, шаг 2).
 *
 * Разделение обязанностей: панель занимается только данными — окно журнала с
 * сервера, клиентская доводка по операции и поиску, пересчёт итога. Разметку
 * рисуют `MovementsFilters`, `MovementsTable` (`≥lg`) и `MovementsCards`
 * (`<lg`), правила отбора и подписи живут в `lib/movements-texts.ts`.
 *
 * Почему окно, а не пагинация: журнал читают «что было за период», а не
 * листают постранично. Берём `limit=500` за выбранный период и честно пишем
 * «Показано N из M»; упёрлись в лимит — экран сам просит сузить период, а не
 * молча показывает кусок (иначе итог по отбору врал бы).
 */

import { TrendingUp } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";

import { MovementsCards } from "./stock/MovementsCards";
import { MovementsFilters } from "./stock/MovementsFilters";
import { MovementsSummary, MovementsSummarySkeleton } from "./stock/MovementsSummary";
import { MovementsTable } from "./stock/MovementsTable";
import type { StockFilters } from "./stock/use-stock-filters";
import { Alert } from "../ui/alert";
import { EmptyState } from "../ui/EmptyState";
import { LIST_SURFACE_CLASS, ListSkeleton } from "../ui/ListStates";
import { NoticeStrip } from "../ui/NoticeStrip";
import { stockApi, type StockMovementDto, type WarehouseDto } from "../../lib/api-wms";
import {
  movementsPeriodLabel,
  movementsPeriodRange,
} from "../../lib/movements-period";
import { movementsUnitSuffix, summarizeMovements } from "../../lib/movements-summary";
import { filterMovements, searchMovements } from "../../lib/movements-filter";
import { movementOperationServerKind } from "../../lib/movements-texts";
import { wmsErrorText } from "../../lib/wms-error-text";
import { BELOW_LG_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";

/** Размер окна журнала: больше сервер и не отдаст осмысленно за один раз. */
const MOVEMENTS_WINDOW = 500;

interface Props {
  filters: StockFilters;
  onChange: (patch: Partial<StockFilters>) => void;
  onReset: () => void;
  dirty: boolean;
  warehouses: WarehouseDto[];
}

export function StockMovementsPanel({
  filters,
  onChange,
  onReset,
  dirty,
  warehouses,
}: Props) {
  const { slug } = useParams<{ slug: string }>();
  const [items, setItems] = useState<StockMovementDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const compact = useMediaQuery(BELOW_LG_QUERY);

  // Серверная часть отбора: склад, вид операции (если он один), период.
  const kind = movementOperationServerKind(filters.operation);
  const range = useMemo(
    () =>
      movementsPeriodRange(filters.period, { from: filters.from, to: filters.to }),
    [filters.period, filters.from, filters.to],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    stockApi
      .movements({
        warehouseId: filters.warehouseId || undefined,
        kind,
        from: range.from,
        to: range.to,
        limit: MOVEMENTS_WINDOW,
      })
      .then((data) => {
        if (cancelled) return;
        setItems(data);
        setError(null);
      })
      .catch((err: unknown) => {
        // Без catch отказ уходил в unhandled rejection, а экран молча замирал
        // на «Движений пока нет».
        if (!cancelled) setError(wmsErrorText(err, "Не удалось загрузить движения"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.warehouseId, kind, range.from, range.to]);

  // SC59 (phase19.31): один поиск — товар, артикул, документ и номер партии.
  // Старый `lot=` из ссылок уже подставлен в `search` (`useStockFilters`).
  // Поиск целиком равен номеру партии — только эта партия, без «-10…-19».
  const visible = useMemo(
    () => searchMovements(filterMovements(items, { operation: filters.operation, search: "" }), filters.search, items),
    [items, filters.operation, filters.search],
  );
  const summary = useMemo(() => summarizeMovements(visible), [visible]);
  const unit = useMemo(() => movementsUnitSuffix(visible), [visible]);
  const windowFull = items.length >= MOVEMENTS_WINDOW;

  return (
    <div className="flex flex-col gap-4">
      <MovementsFilters
        filters={filters}
        onChange={onChange}
        onReset={onReset}
        dirty={dirty}
        warehouses={warehouses}
      />

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        // Заготовки формой под плитки итога и строки журнала (SC69): блок
        // итога есть на экране с первой секунды у любой роли со складом.
        <>
          <MovementsSummarySkeleton />
          <ListSkeleton rows={5} compact={compact} />
        </>
      ) : (
        <>
          {/* SC69: итог — заметными плитками, а не строкой мелкого текста. */}
          <MovementsSummary shown={visible.length} total={items.length} summary={summary} unit={unit} />

          {windowFull ? (
            <NoticeStrip tone="info">
              Показаны последние {MOVEMENTS_WINDOW} движений за выбранный период —
              сузьте период, чтобы увидеть остальные.
            </NoticeStrip>
          ) : null}

          {visible.length === 0 ? (
            <EmptyState
              className={LIST_SURFACE_CLASS}
              icon={TrendingUp}
              variant={dirty ? "filtered" : "empty"}
              title={
                dirty
                  ? "По этому отбору движений нет"
                  : `Движений за ${movementsPeriodLabel(filters.period)} нет`
              }
              description={
                dirty
                  ? "Измените операцию, период или склад."
                  : "Движения появляются после проведения приёмки, списания или сборки."
              }
            />
          ) : (
            <>
              <MovementsCards items={visible} slug={slug} />
              <MovementsTable items={visible} slug={slug} />
            </>
          )}
        </>
      )}
    </div>
  );
}
